import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { fetchRosterProfile } from '../../services/publishingRoster';

// /auth/login is routed by the load balancer's URL map to the separate
// Next.js portal Cloud Run service, not to this React app — plain <a>,
// not a react-router <Link>, since it's a full cross-app navigation.
const CLIENT_PORTAL_URL = '/auth/login';

const FALLBACK_IMG = '/woss-publishing-template/images/Portrait-Image.avif';

const ArrowSvg = () => (
  <>
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 11 9" fill="none" className="icon-1x1-sm-minus">
      <path d="M5.76953 8.91406L5.00391 8.14844L6.44629 6.71289C6.7334 6.42578 7.03418 6.13411 7.34863 5.83789C7.66309 5.53711 7.9707 5.24772 8.27148 4.96973C8.06185 4.97884 7.85221 4.98796 7.64258 4.99707C7.43294 5.00163 7.22331 5.00391 7.01367 5.00391H0V3.91016H7.01367C7.22331 3.91016 7.43294 3.91471 7.64258 3.92383C7.85221 3.92839 8.06185 3.93522 8.27148 3.94434C7.9707 3.66634 7.66309 3.37923 7.34863 3.08301C7.03418 2.78223 6.7334 2.48828 6.44629 2.20117L5.00391 0.765625L5.76953 0L10.2266 4.45703L5.76953 8.91406Z" fill="currentColor" />
    </svg>
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 11 9" fill="none" className="icon-1x1-sm-minus">
      <path d="M5.76953 8.91406L5.00391 8.14844L6.44629 6.71289C6.7334 6.42578 7.03418 6.13411 7.34863 5.83789C7.66309 5.53711 7.9707 5.24772 8.27148 4.96973C8.06185 4.97884 7.85221 4.98796 7.64258 4.99707C7.43294 5.00163 7.22331 5.00391 7.01367 5.00391H0V3.91016H7.01367C7.22331 3.91016 7.43294 3.91471 7.64258 3.92383C7.85221 3.92839 8.06185 3.93522 8.27148 3.94434C7.9707 3.66634 7.66309 3.37923 7.34863 3.08301C7.03418 2.78223 6.7334 2.48828 6.44629 2.20117L5.00391 0.765625L5.76953 0L10.2266 4.45703L5.76953 8.91406Z" fill="currentColor" />
    </svg>
  </>
);

/* Render only the social links that are present */
function SocialLinks({ profile }) {
  const links = [
    { key: 'instagram_url',  label: 'Instagram' },
    { key: 'spotify_url',    label: 'Spotify'   },
    { key: 'apple_url',      label: 'Apple Music'},
    { key: 'youtube_url',    label: 'YouTube'   },
    { key: 'tiktok_url',     label: 'TikTok'    },
    { key: 'twitter_url',    label: 'X / Twitter'},
    { key: 'website_url',    label: 'Website'   },
  ].filter(({ key }) => profile[key]);

  if (!links.length) return null;

  return (
    <div className="project-details_info" style={{ marginTop: '1.5rem' }}>
      {links.map(({ key, label }) => (
        <div key={key} data-load-text="" className="project-details_info-block">
          <span className="text-size-md">{label}</span>
          <a
            href={profile[key]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-size-md text-color-secondary letter-spacing-normal"
            style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            {label}
          </a>
        </div>
      ))}
    </div>
  );
}

export default function RosterProfile() {
  const { slug }   = useParams();
  const [profile, setProfile] = useState(null);
  const [status, setStatus]   = useState('loading'); // loading | ready | notfound | error
  const [imgErr, setImgErr]   = useState(false);

  useEffect(() => {
    if (!slug || !/^[a-z0-9-]+$/.test(slug)) { setStatus('notfound'); return; }
    fetchRosterProfile(slug)
      .then(data => {
        if (!data) { setStatus('notfound'); return; }
        setProfile(data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [slug]);

  /* Dynamic document title */
  useEffect(() => {
    if (profile) {
      document.title = `${profile.display_name} | Woss Publishing`;
    } else if (status === 'notfound') {
      document.title = 'Profile Not Found | Woss Publishing';
    }
    return () => { document.title = 'Woss Publishing | Music Publishing, Rights and Royalty Administration'; };
  }, [profile, status]);

  const imgSrc = (!imgErr && profile?.profile_image_url) ? profile.profile_image_url : FALLBACK_IMG;

  return (
    <div className="page-wrapper">
      <Header />

      <main className="main-wrapper">

        {/* ── Loading ──────────────────────────────────────────────── */}
        {status === 'loading' && (
          <section style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="padding-global">
              <p className="text-size-md text-color-secondary">Loading profile…</p>
            </div>
          </section>
        )}

        {/* ── Not found ────────────────────────────────────────────── */}
        {status === 'notfound' && (
          <section style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="padding-global">
              <div style={{ textAlign: 'center' }}>
                <h1 className="heading-style-h2" style={{ marginBottom: '1rem' }}>Profile not found</h1>
                <p className="text-size-md text-color-secondary" style={{ marginBottom: '2rem' }}>
                  This roster profile does not exist or is not currently published.
                </p>
                <Link to="/#roster" className="button w-inline-block">
                  <span className="text-size-sm">Back to Roster</span>
                  <div className="button_icon"><ArrowSvg /></div>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ── Error ────────────────────────────────────────────────── */}
        {status === 'error' && (
          <section style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="padding-global">
              <div style={{ textAlign: 'center' }}>
                <h1 className="heading-style-h2" style={{ marginBottom: '1rem' }}>Something went wrong</h1>
                <p className="text-size-md text-color-secondary" style={{ marginBottom: '2rem' }}>
                  Unable to load this profile right now. Please try again later.
                </p>
                <Link to="/#roster" className="button w-inline-block">
                  <span className="text-size-sm">Back to Roster</span>
                  <div className="button_icon"><ArrowSvg /></div>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ── Profile ──────────────────────────────────────────────── */}
        {status === 'ready' && profile && (
          <>
            {/* Header — mirrors section_project-details */}
            <header className="section_project-details">
              <div className="padding-global">
                <div className="container-large">
                  <div className="project-details_component">
                    <h1 data-load-text="" className="heading-style-h1">{profile.display_name}</h1>
                    <div className="project-details_content">
                      <div className="project-details_description">
                        {/* Bio or placeholder */}
                        <p data-load-text="" className="text-size-md">
                          {profile.long_bio || profile.short_bio || 'Profile information coming soon.'}
                        </p>

                        {/* Metadata blocks */}
                        <div className="project-details_info">
                          {profile.professional_category && (
                            <div data-load-text="" className="project-details_info-block">
                              <span className="text-size-md">Category</span>
                              <p className="text-size-md text-color-secondary letter-spacing-normal">
                                {profile.professional_category}
                              </p>
                            </div>
                          )}
                          {profile.genres && (
                            <div data-load-text="" className="project-details_info-block">
                              <span className="text-size-md">Genres</span>
                              <p className="text-size-md text-color-secondary letter-spacing-normal">
                                {profile.genres}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Social links — only shown when present */}
                        <SocialLinks profile={profile} />
                      </div>

                      {/* Profile image */}
                      <div className="project-details_visual">
                        <img
                          src={imgSrc}
                          loading="eager"
                          alt={profile.display_name}
                          className="project-details_image"
                          onError={() => setImgErr(true)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Gallery marquee — uses template roster images as visual background */}
            <section className="section_next-project">
              <div className="padding-global">
                <div className="container-large">
                  <div className="basic_heading">
                    <h2 className="heading-style-h2">More from the roster</h2>
                  </div>
                </div>
              </div>
              <div>
                <div className="projects_marquee">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="projects_marquee-list">
                      {[1, 2, 3, 4, 5].map(n => (
                        <a
                          key={n}
                          aria-label="Woss Publishing roster"
                          href="/#roster"
                          className="projects_marquee-item w-inline-block"
                        >
                          <img
                            src={`/woss-publishing-template/images/Project-Thumbnail-${n === 3 ? 'Thumb-3_1Project-Thumb-3' : n}.avif`}
                            loading="lazy"
                            alt="Woss Publishing roster"
                            className="project_marquee-img"
                          />
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="cta">
              <div data-wf--rectangle-background--position="top-left" className="rectangle_wrap">
                <div data-wf--rectangle-shape--variant="light" className="rectangle_shape w-variant-5bf2dcb1-e8c1-b1f1-033d-4af6b1cf8283" />
              </div>
              <div className="theme-color-light">
                <div className="background-color-primary">
                  <div className="padding-global padding-section-large">
                    <div className="container-large">
                      <div className="cta_component">
                        <div className="cta_title">
                          <h2 className="heading-style-h2">Part of Woss Publishing?</h2>
                        </div>
                        <div className="cta_content">
                          <p data-load-text="" className="text-color-secondary">
                            Access your songs, analytics, statements, documents, and account
                            information through the secure Woss Publishing portal.
                          </p>
                          <div className="overflow-hidden">
                            <div>
                              <a href={CLIENT_PORTAL_URL} className="button w-inline-block">
                                <span className="text-size-sm">Access Portal</span>
                                <div className="button_icon"><ArrowSvg /></div>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div data-wf--rectangle-background--position="bottom-right" className="rectangle_wrap w-variant-7c3e154f-11b3-7653-d062-3f526a4f801e">
                <div data-wf--rectangle-shape--variant="light" className="rectangle_shape w-variant-5bf2dcb1-e8c1-b1f1-033d-4af6b1cf8283" />
              </div>
            </section>
          </>
        )}

      </main>

      <Footer />
    </div>
  );
}
